<script>
import objectDiff from 'objectdiff';
import { JSONView } from 'vue-json-component';
import helpers from '../helpers';

export default {
  name: 'AuditLogDetails',
  components: {
    JSONView,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
      default: false,
    },
    logDetails: {
      type: Object,
      required: true,
    },
  },
  computed: {
    prettyRequestPayload() {
      return this.logDetails.request_payload
        ? JSON.parse(this.logDetails.request_payload)
        : {};
    },
    prettyResponsePayload() {
      return this.logDetails.response_payload
        ? JSON.parse(this.logDetails.response_payload)
        : {};
    },
    isDiffAvailable() {
      return (
        this.logDetails.action_type === 'modification'
        && !helpers.isNullOrEmpty(this.logDetails.entity_id)
        && !helpers.isNullOrEmpty(this.logDetails.diff_uuid)
        && this.logDetails.diff_uuid !== 'no-diff'
      );
    },
  },
  methods: {
    diffPayloads(obj1, obj2) {
      const parsedObj1 = JSON.parse(obj1);
      const parsedObj2 = JSON.parse(obj2);

      return objectDiff.convertToXMLString(
        objectDiff.diffOwnProperties(
          (parsedObj1?.payload ?? parsedObj1),
          (parsedObj2?.payload ?? parsedObj2),
        ),
      );
    },
  },
};
</script>

<template>
  <b-modal
    id="log-details-modal"
    ref="log-details-modal"
    title="Audit Details"
    size="xl"
    hide-footer
    :visible="isVisible"
    @hide="$emit('hide')"
  >
    <b-card>
      <b-row>
        <b-col>
          <ul class="list-unstyled mb-0">
            <li><b>uuid:</b> {{ logDetails.uuid }}</li>
            <li><b>Application Name:</b> {{ logDetails.application_name }}</li>
            <li><b>Actor ID:</b> {{ logDetails.actor_id }}</li>
            <li><b>Target ID:</b> {{ logDetails.target_id }}</li>
            <li><b>Entity ID:</b> {{ logDetails.entity_id }}</li>
            <li><b>Created At:</b> {{ logDetails.created_at }}</li>
            <li>
              <b>Tags: </b>
              <b-badge
                v-for="tag in logDetails.tags"
                :key="tag"
                variant="primary"
                class="mr-1"
              >
                {{ tag }}
              </b-badge>
            </li>
          </ul>
        </b-col>
        <b-col>
          <ul class="list-unstyled mb-0">
            <li><b>Transaction ID:</b> {{ logDetails.transaction_id }}</li>
            <li><b>Client ID:</b> {{ logDetails.client_id }}</li>
            <li><b>Actor Type:</b> {{ logDetails.actor_type }}</li>
            <li><b>Action Type:</b> {{ logDetails.action_type }}</li>
            <li><b>Target Type:</b> {{ logDetails.target_type }}</li>
            <li><b>Entity Type:</b> {{ logDetails.entity_type }}</li>
            <li><b>Completed At:</b> {{ logDetails.completed_at }}</li>
          </ul>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      no-body
      class="mt-2"
    >
      <b-tabs card>
        <b-tab
          id="tab-details"
          title="Details"
          active
        >
          <b-row>
            <b-col>
              <b-card-title>Request Payload</b-card-title>
              <JSONView
                :data="prettyRequestPayload"
                :max-depth="1"
              />
            </b-col>
            <b-col>
              <b-card-title>Response Payload</b-card-title>
              <JSONView
                :data="prettyResponsePayload"
                :max-depth="1"
              />
            </b-col>
          </b-row>
        </b-tab>

        <b-tab
          v-if="isDiffAvailable"
          title="Response differences"
          @click="$emit('requested-diff', logDetails.uuid)"
        >
          <div
            v-if="logDetails.diffData.isLoading"
            class="text-center text-secondary"
          >
            <b-spinner class="align-middle mr-2" />
            <strong>Loading...</strong>
          </div>

          <div
            v-else-if="!logDetails.diffData.isLoading && logDetails.diffData.data.res.length === 0"
            class="text-center text-danger"
          >
            <strong>There was an error while trying to fetch response differences</strong>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html vue/max-attributes-per-line -->
          <pre v-else class="diff-viewer" v-html="diffPayloads(...logDetails.diffData.data.res)" />
        </b-tab>
      </b-tabs>
    </b-card>
  </b-modal>
</template>

<style lang="scss">
  .diff-viewer {
    .diff {
      display: inline-block;
    }

    .diff-level {
      margin-left: 1.6em;
    }

    .diff-holder {
      color: #666;
      margin: 0;
    }

    .diff-holder span {
      color: #AAA;
    }

    del.diff,
    ins.diff {
      text-decoration: none;
    }

    del.diff {
      color: #b30000;
      background: #fadad7;
    }

    ins.diff {
      background: #eaf2c2;
      color: #406619;
    }

    del.diff-key {
      border: 1px solid #f8a4a4;
    }

    ins.diff-key {
      border: 1px solid #a3ce4c;
      margin-top: -1px;
      position: relative;
    }

    ins.diff span {
      color: #AABF40;
    }

    del.diff span {
      color: #EE8177;
    }

    height: 100%;
    width: 100%;
    margin: auto;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-all !important;
  }
</style>
